import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
  Typography,
  CircularProgress,
  Box,
  Tooltip,
} from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';
import { BRAND_COLORS } from '../../styles/theme';
import { ROLE_OPTIONS, UserRoleType } from '../../config/roles';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { useTranslation } from '../../i18n';
import { useCreateInvitationMutation } from './adminTools.mutations';

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onInviteSent: () => void;
}

export const InviteUserDialog: React.FC<InviteUserDialogProps> = ({
  open,
  onClose,
  onInviteSent,
}) => {
  const { showSuccess } = useErrorHandler();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<UserRoleType[]>([]);
  const [error, setError] = useState('');

  const createInvitationMutation = useCreateInvitationMutation();

  // Map ROLE_OPTIONS to include translated labels
  const translatedRoleDefinitions = ROLE_OPTIONS.map((role) => ({
    value: role.value,
    label: t(role.labelKey),
    description: t(role.descKey),
  }));

  const handleRoleToggle = (role: UserRoleType) => {
    if (selectedRoles.includes(role)) {
      setSelectedRoles(selectedRoles.filter((r) => r !== role));
    } else {
      setSelectedRoles([...selectedRoles, role]);
    }
  };

  const validateForm = (): boolean => {
    if (!email.trim()) {
      setError(t('adminToolsPage.inviteUser.validation.emailRequired'));
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('adminToolsPage.inviteUser.validation.invalidEmail'));
      return false;
    }

    if (selectedRoles.length === 0) {
      setError(t('adminToolsPage.inviteUser.validation.selectRole'));
      return false;
    }

    return true;
  };

  const handleInvite = async () => {
    setError('');

    if (!validateForm()) {
      return;
    }

    createInvitationMutation.mutate(
      { email, roles: selectedRoles },
      {
        onSuccess: () => {
          const successMsg = `Invitation sent successfully to ${email}!`;
          showSuccess(successMsg);
          setEmail('');
          setSelectedRoles([]);

          // Call the callback to refresh the parent component
          onInviteSent();

          // Close after 2 seconds
          setTimeout(() => {
            handleClose();
          }, 2000);
        },
      }
    );
  };

  const handleClose = () => {
    setEmail('');
    setSelectedRoles([]);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon sx={{ color: BRAND_COLORS.forestGreen.main }} />
          {t('adminToolsPage.inviteUser.dialogTitle')}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label={t('adminToolsPage.inviteUser.emailAddress')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('adminToolsPage.inviteUser.emailPlaceholder')}
            disabled={createInvitationMutation.isPending}
            margin="normal"
            helperText={t('adminToolsPage.inviteUser.emailHelperText')}
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            {t('adminToolsPage.inviteUser.selectRoles')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('adminToolsPage.inviteUser.selectRolesHelper')}
          </Typography>

          <FormGroup>
            {translatedRoleDefinitions.map((role) => (
              <Tooltip key={role.value} title={role.description} placement="right">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedRoles.includes(role.value)}
                      onChange={() => handleRoleToggle(role.value)}
                      disabled={createInvitationMutation.isPending}
                      sx={{
                        color: BRAND_COLORS.forestGreen.main,
                        '&.Mui-checked': {
                          color: BRAND_COLORS.forestGreen.main,
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">{role.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {role.description}
                      </Typography>
                    </Box>
                  }
                />
              </Tooltip>
            ))}
          </FormGroup>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            ℹ️ {t('adminToolsPage.inviteUser.invitationNote')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={createInvitationMutation.isPending}>
          {t('adminToolsPage.inviteUser.cancel')}
        </Button>
        <Button
          onClick={handleInvite}
          variant="contained"
          disabled={
            createInvitationMutation.isPending || !email.trim() || selectedRoles.length === 0
          }
          startIcon={
            createInvitationMutation.isPending ? <CircularProgress size={16} /> : <EmailIcon />
          }
          sx={{
            backgroundColor: BRAND_COLORS.forestGreen.main,
            '&:hover': {
              backgroundColor: BRAND_COLORS.forestGreen.main,
              opacity: 0.8,
            },
          }}
        >
          {createInvitationMutation.isPending
            ? t('adminToolsPage.inviteUser.sending')
            : t('adminToolsPage.inviteUser.sendInvitation')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
